import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, Col, Row, Space, Tag, Tooltip } from 'antd'
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    PlusOutlined,
    RollbackOutlined,
    ShoppingOutlined,
    StarOutlined,
    TagsOutlined,
} from '@ant-design/icons'
import controller, { DEFAULT_COLOR, ROLE_COLORS, StatusRole, allowsCancel, allowsOrderReview, capturesPayment, confirmsPurchase, refundsOnCancel, statusRole } from './controller'
import { STATUS_ICONS, StatusIcon } from './OrderStatusIcons'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { CardList, cleanText, ListCard, ListChip, ListTone } from '../../../components/card_list/CardList'
import './order_status.less'

export const statusColor = (status: any): string => status?.color_hex || DEFAULT_COLOR

export const hexToRgba = (hex: string, alpha: number) => {
    const clean = (hex ?? '').replace('#', '')
    if (!/^[0-9a-fA-F]{6}$/.test(clean)) return `rgba(114, 100, 214, ${alpha})`

    const n = parseInt(clean, 16)

    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/** Иконка статуса в его цвете; без иконки — просто цветная точка */
export const StatusGlyph = ({ status }: { status: any }) => {
    const color = statusColor(status)

    return STATUS_ICONS.some(el => el.key === status?.icon)
        ? <StatusIcon icon={status.icon} color={color} />
        : <span style={{ width: 10, height: 10, borderRadius: '50%', background: `#${color}` }} />
}

export const StatusIconTile = ({ status, size = 40 }: { status: any, size?: number }) => (
    <div
        className="os-icon-tile"
        style={{ width: size, height: size, borderRadius: size * 0.3, background: hexToRgba(statusColor(status), 0.12) }}
    >
        <StatusGlyph status={status} />
    </div>
)

export const RoleTag = ({ role }: { role: StatusRole }) => (
    <Tag color={ROLE_COLORS[role]} style={{ margin: 0 }}>{i.t(`orderStatus:roles.${role}`)}</Tag>
)

const SYSTEM_ROLES: StatusRole[] = ['initial', 'completed', 'canceled']

// Без статуса с ролью «Начальный» новым заказам нечего присвоить, поэтому
// незанятые роли подсвечиваем прямо над списком.
const RoleTile = observer(({ role }: { role: StatusRole }) => {
    const history = useHistory()
    const owner = controller.list.find((el: any) => statusRole(el) === role)

    return (
        <Col xs={24} md={8}>
            <div
                className={`os-role-tile${owner ? ' os-role-tile__link' : ''}`}
                onClick={() => owner && history.push(`/shop/statuses/${owner.id}`)}
            >
                <div className="os-role-tile__label">
                    <Space size={6}>
                        <Tag color={ROLE_COLORS[role]} style={{ margin: 0 }}>{i.t(`orderStatus:roles.${role}`)}</Tag>
                        <span className="os-hint">{i.t(`orderStatus:roleHints.${role}`)}</span>
                    </Space>
                </div>
                <div className="os-role-tile__value">
                    {owner
                        ? <>
                            <StatusIconTile status={owner} size={28} />
                            <span>{owner.name}</span>
                        </>
                        : <span style={{ color: '#d48806' }}>
                            <ExclamationCircleOutlined style={{ marginRight: 6 }} />
                            {i.t('orderStatus:roleMissing')}
                        </span>
                    }
                </div>
            </div>
        </Col>
    )
})

const ROLE_TONES: Record<StatusRole, ListTone | undefined> = {
    none: undefined,
    initial: 'info',
    completed: 'success',
    canceled: 'danger',
}

// Что делает статус: отмена клиентом, возврат, списание холда, оценка заказа, покупка для отзыва
const behaviorTags = (status: any) => [
    allowsCancel(status) && { key: 'cancel', icon: <CloseCircleOutlined /> },
    refundsOnCancel(status) && { key: 'refund', icon: <RollbackOutlined /> },
    capturesPayment(status) && { key: 'capture', icon: <CheckCircleOutlined /> },
    allowsOrderReview(status) && { key: 'review', icon: <StarOutlined /> },
    confirmsPurchase(status) && { key: 'purchase', icon: <ShoppingOutlined /> },
].filter(Boolean) as { key: string, icon: React.ReactNode }[]

const StatusCard = ({ status }: { status: any }) => {
    const role = statusRole(status)
    // Статус с системной ролью удалить нельзя — роль сначала надо передать другому
    const locked = role !== 'none'
    const idCrm = cleanText(status.id_crm)
    const href = `/shop/statuses/${status.id}`

    return (
        <ListCard
            href={href}
            media={<StatusIconTile status={status} size={52} />}
            title={status.name}
            titleHint={status.name}
            subtitle={<>
                ID {status.id}
                {idCrm && <> · {i.t('orderStatus:fields.idCrm')} {idCrm}</>}
            </>}
            chips={<>
                <ListChip tone={ROLE_TONES[role]}>{i.t(`orderStatus:roles.${role}`)}</ListChip>
                {behaviorTags(status).map(tag =>
                    <ListChip key={tag.key} icon={tag.icon}>{i.t(`orderStatus:tags.${tag.key}`)}</ListChip>
                )}
            </>}
            actions={
                // Обёртка гасит клик: у отключённой кнопки внутри Tooltip он
                // приходит на span-обёртку и иначе открыл бы статус
                <Space size={8} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <Tooltip title={i.t('def:actions.edit')}>
                        <Button shape="circle" icon={<EditOutlined />} href={href} />
                    </Tooltip>
                    <Tooltip title={locked ? i.t('orderStatus:roleLocked') : ''}>
                        <Button
                            shape="circle"
                            danger
                            disabled={locked}
                            icon={<DeleteOutlined />}
                            onClick={() => controller.remove(status.id)}
                        />
                    </Tooltip>
                </Space>
            }
        />
    )
}

export const OrderStatusesList = observer(() => {

    const history = useHistory()

    useEffect(() => {
        controller.getList()
    }, [])

    const buttons = [
        { title: i.t('def:actions.create'), click: () => history.push('/shop/statuses/0'), type: 'primary' },
    ]

    return (
        <>
            <Titles
                title={i.t('orderStatus:title')}
                buttons={buttons}
                breadcrumb={[{ title: i.t('orderStatus:title') }]}
            />

            <div className="os-hint" style={{ fontSize: 13, marginBottom: 16, maxWidth: 760 }}>
                {i.t('orderStatus:hint')}
            </div>

            {controller.listLoaded && (
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    {SYSTEM_ROLES.map(role => <RoleTile role={role} key={role} />)}
                </Row>
            )}

            <CardList
                items={controller.list}
                count={controller.list.length}
                loading={!controller.listLoaded}
                renderItem={(status: any) => <StatusCard status={status} />}
                empty={{
                    icon: <TagsOutlined />,
                    title: i.t('orderStatus:empty'),
                    action: <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push('/shop/statuses/0')}>{i.t('def:actions.create')}</Button>,
                }}
            />
        </>
    )
})
